import { FERTILITY_COST_CATALOG } from "./catalog";
import type {
  CostCatalogEntry,
  EstimateLineId,
  FertilityCostCatalog,
} from "./catalog";
import type { TreatmentType } from "@src/packages/homepage/utils/costEstimator";

export type EstimateLocationInput = {
  countryCode: string;
  stateCode?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  clinicIds?: string[];
};

export type ResolvedEstimatePrice = {
  amount: number;
  lineId: EstimateLineId;
  unit: CostCatalogEntry["unit"];
  currency: CostCatalogEntry["currency"];
  catalogVersion: string;
  resolution: "clinic" | "nearby-clinics" | "city" | "state" | "country";
  entries: CostCatalogEntry[];
  confidence: CostCatalogEntry["confidence"];
};

type ResolvePriceParams = {
  lineId: EstimateLineId;
  location: EstimateLocationInput;
  catalog?: FertilityCostCatalog;
};

function normalize(value?: string) {
  return value?.trim().toLowerCase();
}

function confidenceRank(confidence: CostCatalogEntry["confidence"]) {
  if (confidence === "high") return 3;
  if (confidence === "medium") return 2;
  return 1;
}

function lowestConfidence(entries: CostCatalogEntry[]) {
  return entries.reduce<CostCatalogEntry["confidence"]>(
    (lowest, entry) =>
      confidenceRank(entry.confidence) < confidenceRank(lowest)
        ? entry.confidence
        : lowest,
    "high",
  );
}

function averageEntries(
  entries: CostCatalogEntry[],
  resolution: ResolvedEstimatePrice["resolution"],
  catalogVersion: string,
): ResolvedEstimatePrice | null {
  if (entries.length === 0) return null;
  const amount = Math.round(
    entries.reduce((sum, entry) => sum + entry.amount, 0) / entries.length,
  );
  const firstEntry = entries[0]!;

  return {
    amount,
    lineId: firstEntry.lineId,
    unit: firstEntry.unit,
    currency: firstEntry.currency,
    catalogVersion,
    resolution,
    entries,
    confidence: lowestConfidence(entries),
  };
}

function distanceMiles(
  from: Required<Pick<EstimateLocationInput, "latitude" | "longitude">>,
  to: Required<Pick<CostCatalogEntry["location"], "latitude" | "longitude">>,
) {
  const earthRadiusMiles = 3958.8;
  const fromLat = (from.latitude * Math.PI) / 180;
  const toLat = (to.latitude * Math.PI) / 180;
  const deltaLat = ((to.latitude - from.latitude) * Math.PI) / 180;
  const deltaLon = ((to.longitude - from.longitude) * Math.PI) / 180;
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(fromLat) *
      Math.cos(toLat) *
      Math.sin(deltaLon / 2) *
      Math.sin(deltaLon / 2);

  return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function resolveEstimatePrice({
  lineId,
  location,
  catalog = FERTILITY_COST_CATALOG,
}: ResolvePriceParams): ResolvedEstimatePrice {
  const candidates = catalog.entries.filter(
    (entry) =>
      entry.lineId === lineId &&
      entry.location.countryCode === location.countryCode,
  );
  const selectedClinicEntries =
    location.clinicIds && location.clinicIds.length > 0
      ? candidates.filter(
          (entry) =>
            entry.location.scope === "clinic" &&
            entry.location.clinicId != null &&
            location.clinicIds!.includes(entry.location.clinicId),
        )
      : [];
  const selectedClinicAverage = averageEntries(
    selectedClinicEntries,
    "clinic",
    catalog.version,
  );

  if (selectedClinicAverage) return selectedClinicAverage;

  if (location.latitude != null && location.longitude != null) {
    const nearbyClinicEntries = candidates
      .filter(
        (entry) =>
          entry.location.scope === "clinic" &&
          entry.location.latitude != null &&
          entry.location.longitude != null,
      )
      .map((entry) => ({
        entry,
        distance: distanceMiles(
          {
            latitude: location.latitude!,
            longitude: location.longitude!,
          },
          {
            latitude: entry.location.latitude!,
            longitude: entry.location.longitude!,
          },
        ),
      }))
      .filter(({ distance }) => distance <= 250)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 5)
      .map(({ entry }) => entry);
    const nearbyAverage = averageEntries(
      nearbyClinicEntries,
      "nearby-clinics",
      catalog.version,
    );

    if (nearbyAverage) return nearbyAverage;
  }

  const cityEntries = candidates.filter(
    (entry) =>
      entry.location.scope === "city" &&
      normalize(entry.location.city) === normalize(location.city) &&
      entry.location.stateCode === location.stateCode,
  );
  const cityAverage = averageEntries(cityEntries, "city", catalog.version);

  if (cityAverage) return cityAverage;

  const stateEntries = candidates.filter(
    (entry) =>
      entry.location.scope === "state" &&
      entry.location.stateCode === location.stateCode,
  );
  const stateAverage = averageEntries(stateEntries, "state", catalog.version);

  if (stateAverage) return stateAverage;

  const countryEntries = candidates.filter(
    (entry) => entry.location.scope === "country",
  );
  const countryAverage = averageEntries(
    countryEntries,
    "country",
    catalog.version,
  );

  if (countryAverage) return countryAverage;

  throw new Error(`No estimate price found for ${lineId}`);
}

export function resolveEstimateAmount(
  lineId: EstimateLineId,
  location: EstimateLocationInput,
) {
  return resolveEstimatePrice({ lineId, location }).amount;
}

/**
 * Whether the catalog can price this location below the country level (a
 * clinic/city/state row actually matches it). Mirrors the cascade match in
 * `resolveEstimatePrice`. Used to decide location-specific copy: with a
 * country-only catalog this is always false, so the estimate is honestly
 * labeled a national average instead of claiming "in this location". Becomes
 * true automatically once matching sub-country price rows are ingested.
 */
export function isLocationSpecificEstimate(
  location: EstimateLocationInput,
  catalog: FertilityCostCatalog = FERTILITY_COST_CATALOG,
): boolean {
  return catalog.entries.some((entry) => {
    if (entry.location.countryCode !== location.countryCode) return false;
    switch (entry.location.scope) {
      case "clinic":
        return (
          entry.location.clinicId != null &&
          (location.clinicIds ?? []).includes(entry.location.clinicId)
        );
      case "city":
        return (
          normalize(entry.location.city) === normalize(location.city) &&
          entry.location.stateCode === location.stateCode
        );
      case "state":
        return entry.location.stateCode === location.stateCode;
      default:
        return false;
    }
  });
}

export function getMedicationLineId(
  treatment: TreatmentType,
): EstimateLineId | null {
  if (treatment === "sperm_freezing") return null;
  if (treatment === "fet") return "medications-fet";
  if (treatment === "iui") return "medications-iui";
  if (treatment === "ovulation_induction") return "medications-oi";
  return "medications-stimulation";
}

export function hasMedicationEstimate(treatment: TreatmentType) {
  return getMedicationLineId(treatment) != null;
}

export function roundModeledEstimate(amount: number) {
  const absAmount = Math.abs(amount);
  const increment = absAmount >= 10000 ? 250 : absAmount >= 1000 ? 100 : 50;
  return Math.round(amount / increment) * increment;
}
